import React, { useState, useMemo, useCallback } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryKeys } from '../../lib/queryKeys';
import toast from 'react-hot-toast';
import { EnhancedPartner, ProvisioningStatus } from '../../types/partner';
import { ResourceStatusBadge } from '../shared/StatusBadge';
import { Connection } from '../../types/connection';
import { Task, TaskExecution, UpdateTaskRequest, TransferDirection } from '../../types/task';
import { StatusIndicator, StatusType } from '../shared/StatusIndicators';
import { Button } from '../shared/Button';
import { partnersApi, DependencyError, Dependency } from '../../services/api/partners';
import { CollapsibleSection } from '../shared/CollapsibleSection';
import EditPartner from './EditPartner';
import ConnectionDetails from '../connections/ConnectionDetails';
import { TaskDetails } from '../tasks/TaskDetails';
import { TaskEditor } from '../tasks/TaskEditor';
import { tasksApi } from '../../services/api/tasks';
import { useTaskActions } from '../../hooks/useTaskActions';
import { CascadeDeleteDialog } from '../shared/CascadeDelete';
import { AuditLogViewer } from '../audit';
import { RoleGuard } from '../RoleGuard';
// THUB-186: timezone-aware shared formatter (was new Date(x).toLocaleString()).
import { formatDateTime, activeTimezone } from '../../utils/formatDateTime';
// Version conflict temporarily disabled for debugging
// import { useVersionConflict } from '../../hooks/useVersionConflict';

interface PartnerDetailsProps {
  partner: EnhancedPartner;
  onClose: () => void;
  onUpdate: () => void | Promise<void>;
}

type TabType = 'overview' | 'connections' | 'tasks' | 'activity' | 'audit';

// Stable empty references: `?? []` would be a new array on every render.
const NO_CONNECTIONS: Connection[] = [];
const NO_TASKS: Task[] = [];

// Reusable component that uses ConnectionList table logic for partner connections
interface PartnerConnectionListProps {
  connections: Connection[];
  partners: { partnerId: string; name: string }[];
  onConnectionSelect: (connection: Connection) => void;
}

// Simple task list component for use in modals (similar to PartnerConnectionList)
interface PartnerTaskListProps {
  tasks: Task[];
  onTaskSelect: (task: Task) => void;
}

function PartnerTaskList({ tasks, onTaskSelect }: PartnerTaskListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Task>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getStatusColor = (status: string): StatusType => {
    switch (status) {
      case 'enabled': return 'connected';
      case 'running': return 'info';
      case 'completed': return 'connected';
      case 'failed': return 'error';
      case 'disabled': return 'neutral';
      default: return 'info';
    }
  };

  // Filter, search, and sort data
  const filteredAndSortedData = useMemo(() => {
    let result = [...tasks];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(t =>
        t.name.toLowerCase().includes(search) ||
        t.partnerName.toLowerCase().includes(search) ||
        t.connectionName.toLowerCase().includes(search) ||
        t.status.toLowerCase().includes(search) ||
        t.transferDirection.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Number comparison
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [tasks, searchTerm, sortField, sortOrder]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (field: keyof Task) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: keyof Task) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm border border-neutral-200 dark:border-slate-700">
      {/* Search and controls */}
      <div className="p-4 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-800">
        <div className="relative max-w-xs">
          <input
            type="text"
            placeholder="Search tasks..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="
              w-full px-3 py-2
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-md text-sm
              text-neutral-900 dark:text-slate-100
              placeholder:text-neutral-400 dark:placeholder:text-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="
                absolute right-2 top-1/2 -translate-y-1/2
                w-5 h-5 flex items-center justify-center
                text-neutral-400 hover:text-neutral-600
                dark:text-slate-500 dark:hover:text-slate-300
              "
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 dark:bg-slate-800">
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('name')}
              >
                Task Name {getSortIcon('name')}
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('transferDirection')}
              >
                Direction {getSortIcon('transferDirection')}
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-10 text-center text-neutral-500 dark:text-slate-400 italic">
                  {searchTerm ? 'No tasks match your search' : 'No tasks found'}
                </td>
              </tr>
            ) : (
              paginatedData.map(task => (
                <tr
                  key={task.taskId}
                  onClick={() => onTaskSelect(task)}
                  className="
                    border-b border-neutral-200 dark:border-slate-700
                    hover:bg-primary-50 dark:hover:bg-primary-900/20
                    cursor-pointer transition-colors duration-150
                  "
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-neutral-900 dark:text-slate-100">{task.name}</span>
                      {task.configuration?.sourceDirectory && (
                        <span className="text-xs text-neutral-500 dark:text-slate-400">{task.configuration.sourceDirectory}</span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="
                      inline-block px-2 py-0.5 rounded-full
                      text-xs font-medium uppercase tracking-wide
                      bg-neutral-100 dark:bg-slate-700
                      text-neutral-700 dark:text-slate-300
                    ">
                      {task.transferDirection}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator
                      status={getStatusColor(task.status)}
                      label={task.status}
                      size="small"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onTaskSelect(task);
                      }}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="
          flex flex-col md:flex-row items-center justify-between gap-4 p-4
          bg-neutral-50 dark:bg-slate-800
          border-t border-neutral-200 dark:border-slate-700
        ">
          <div className="text-sm text-neutral-600 dark:text-slate-400">
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredAndSortedData.length)} to{' '}
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} tasks
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏮
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ◀
            </button>

            <span className="mx-3 text-sm font-medium text-neutral-700 dark:text-slate-300">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ▶
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(Math.max(1, totalPages))}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏭
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-slate-400">
            <label>Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="
                px-2 py-1 rounded border
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
              "
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerConnectionList({ connections, partners, onConnectionSelect }: PartnerConnectionListProps) {
  const [searchTerm, setSearchTerm] = useState('');
  const [sortField, setSortField] = useState<keyof Connection>('name');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const getStatusColor = (status: string): StatusType => {
    switch (status) {
      case 'ready': return 'connected';
      case 'issues': return 'error';
      case 'setting_up': return 'warning';
      case 'idle': return 'info';
      case 'not_used': return 'neutral';
      case 'unknown': return 'info';
      default: return 'info';
    }
  };

  // Filter, search, and sort data (reusing ConnectionList logic)
  const filteredAndSortedData = useMemo(() => {
    let result = [...connections];

    // Apply search filter
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      result = result.filter(c =>
        c.name.toLowerCase().includes(search) ||
        c.partnerName.toLowerCase().includes(search) ||
        c.hostingType.toLowerCase().includes(search) ||
        c.transferMethod.toLowerCase().includes(search) ||
        c.status.toLowerCase().includes(search)
      );
    }

    // Apply sorting
    result.sort((a, b) => {
      let aVal: any = a[sortField];
      let bVal: any = b[sortField];

      // Handle nested taskCounts
      if (sortField === 'taskCounts' as keyof Connection) {
        aVal = a.taskCounts?.total || 0;
        bVal = b.taskCounts?.total || 0;
      }

      // Handle null/undefined
      if (aVal == null) aVal = '';
      if (bVal == null) bVal = '';

      // String comparison
      if (typeof aVal === 'string' && typeof bVal === 'string') {
        return sortOrder === 'asc'
          ? aVal.localeCompare(bVal)
          : bVal.localeCompare(aVal);
      }

      // Number comparison
      if (sortOrder === 'asc') {
        return aVal > bVal ? 1 : -1;
      } else {
        return aVal < bVal ? 1 : -1;
      }
    });

    return result;
  }, [connections, searchTerm, sortField, sortOrder]);

  // Paginated data
  const paginatedData = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return filteredAndSortedData.slice(start, end);
  }, [filteredAndSortedData, currentPage, pageSize]);

  const totalPages = Math.ceil(filteredAndSortedData.length / pageSize);

  const handleSort = (field: keyof Connection) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getSortIcon = (field: keyof Connection) => {
    if (sortField !== field) return '↕';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg overflow-hidden shadow-sm border border-neutral-200 dark:border-slate-700">
      {/* Search and controls */}
      <div className="p-4 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-800">
        <div className="relative max-w-xs">
          <input
            type="text"
            placeholder="Search connections..."
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            className="
              w-full px-3 py-2
              bg-white dark:bg-slate-800
              border border-neutral-300 dark:border-slate-600
              rounded-md text-sm
              text-neutral-900 dark:text-slate-100
              placeholder:text-neutral-400 dark:placeholder:text-slate-500
              focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent
              transition-all duration-200
            "
          />
          {searchTerm && (
            <button
              className="
                absolute right-2 top-1/2 -translate-y-1/2
                w-5 h-5 flex items-center justify-center
                text-neutral-400 hover:text-neutral-600
                dark:text-slate-500 dark:hover:text-slate-300
              "
              onClick={() => setSearchTerm('')}
            >
              ×
            </button>
          )}
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-neutral-50 dark:bg-slate-800">
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('name')}
              >
                Connection Name {getSortIcon('name')}
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('hostingType')}
              >
                Type {getSortIcon('hostingType')}
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('taskCounts' as keyof Connection)}
              >
                Tasks {getSortIcon('taskCounts' as keyof Connection)}
              </th>
              <th
                className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 cursor-pointer hover:bg-neutral-100 dark:hover:bg-slate-800 select-none whitespace-nowrap"
                onClick={() => handleSort('status')}
              >
                Status {getSortIcon('status')}
              </th>
              <th className="px-4 py-3 text-left font-semibold text-neutral-700 dark:text-slate-300 border-b-2 border-neutral-200 dark:border-slate-700 whitespace-nowrap">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {paginatedData.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-4 py-10 text-center text-neutral-500 dark:text-slate-400 italic">
                  {searchTerm ? 'No connections match your search' : 'No connections found'}
                </td>
              </tr>
            ) : (
              paginatedData.map(connection => (
                <tr
                  key={connection.connectionId}
                  onClick={() => onConnectionSelect(connection)}
                  className="
                    border-b border-neutral-200 dark:border-slate-700
                    hover:bg-primary-50 dark:hover:bg-primary-900/20
                    cursor-pointer transition-colors duration-150
                  "
                >
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-1">
                      <span className="font-medium text-neutral-900 dark:text-slate-100">{connection.name}</span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-xs text-neutral-600 dark:text-slate-400">
                      {connection.hostingType} / {connection.transferMethod}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-semibold text-neutral-900 dark:text-slate-100">
                        {connection.taskCounts?.total || 0}
                      </span>
                      <span className="text-xs text-success-600 dark:text-success-400">
                        {connection.taskCounts?.enabled || 0} enabled
                      </span>
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <StatusIndicator
                      status={getStatusColor(connection.status)}
                      label={connection.status}
                      size="small"
                    />
                  </td>
                  <td className="px-4 py-3">
                    <Button
                      variant="primary"
                      size="sm"
                      onClick={(e) => {
                        e.stopPropagation();
                        onConnectionSelect(connection);
                      }}
                    >
                      View Details
                    </Button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {filteredAndSortedData.length > 0 && (
        <div className="
          flex flex-col md:flex-row items-center justify-between gap-4 p-4
          bg-neutral-50 dark:bg-slate-800
          border-t border-neutral-200 dark:border-slate-700
        ">
          <div className="text-sm text-neutral-600 dark:text-slate-400">
            Showing {Math.min((currentPage - 1) * pageSize + 1, filteredAndSortedData.length)} to{' '}
            {Math.min(currentPage * pageSize, filteredAndSortedData.length)} of {filteredAndSortedData.length} connections
          </div>

          <div className="flex items-center gap-2">
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏮
            </button>
            <button
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(currentPage - 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ◀
            </button>

            <span className="mx-3 text-sm font-medium text-neutral-700 dark:text-slate-300">
              Page {currentPage} of {totalPages || 1}
            </span>

            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(currentPage + 1)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ▶
            </button>
            <button
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(totalPages)}
              className="
                px-3 py-1.5 rounded border text-sm
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
                hover:bg-neutral-100 dark:hover:bg-slate-700
                disabled:opacity-50 disabled:cursor-not-allowed
                transition-colors duration-150
              "
            >
              ⏭
            </button>
          </div>

          <div className="flex items-center gap-2 text-sm text-neutral-600 dark:text-slate-400">
            <label>Show:</label>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(parseInt(e.target.value));
                setCurrentPage(1);
              }}
              className="
                px-2 py-1 rounded border
                bg-white dark:bg-slate-800
                border-neutral-300 dark:border-slate-600
                text-neutral-700 dark:text-slate-300
              "
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={20}>20</option>
              <option value={50}>50</option>
            </select>
            <span>per page</span>
          </div>
        </div>
      )}
    </div>
  );
}

function PartnerDetails({ partner, onClose, onUpdate }: PartnerDetailsProps) {
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [isEditing, setIsEditing] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showCascadeDialog, setShowCascadeDialog] = useState(false);
  const [cascadeDependencies, setCascadeDependencies] = useState<Dependency[]>([]);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  // THUB-299: was two useState lists, a dependenciesLoading flag, a `loadedPartnerRef`
  // "have I loaded this partner yet" guard, and a mount effect carrying an exhaustive-deps
  // disable. Per-key caching IS that guard -- a re-render re-reads nothing, and a partner change
  // is a different key -- so the ref has nothing left to do.
  //
  // Keyed under `connections` / `tasks` rather than under `partners`, because these ARE
  // connection and task lists: that is what lets App.tsx's coalesced invalidation reach an open
  // modal. See the THUB-182 note below, whose workaround this removes the need for.
  const connectionsQuery = useQuery({
    queryKey: queryKeys.connections.byPartner(partner.partnerId),
    queryFn: () => partnersApi.getPartnerConnections(partner.partnerId),
  });
  const tasksQuery = useQuery({
    queryKey: queryKeys.tasks.byPartner(partner.partnerId),
    queryFn: () => partnersApi.getPartnerTasks(partner.partnerId),
  });

  // On failure fall back to what the partner prop already carries, NOT to an empty list --
  // reporting zero dependencies when the read failed would make a cascade delete look safe when
  // it is not. This is what the original's catch branch did.
  const partnerConnections: Connection[] =
    connectionsQuery.data ?? partner.connections ?? NO_CONNECTIONS;
  const partnerTasks: Task[] = tasksQuery.data ?? partner.tasks ?? NO_TASKS;
  const dependenciesLoading = connectionsQuery.isPending || tasksQuery.isPending;

  // Nested modal states
  const [selectedConnection, setSelectedConnection] = useState<Connection | null>(null);
  // THUB-162: the partner-task list object is a partial Task (its mapper drops
  // deliveryMode/prefix/snsTopicArn/integration), so rendering TaskDetails off it
  // showed a stale "still provisioning" Integration Guide and dropped the
  // Schedule card. We open the modal, fetch the FULL task, and render that.
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [fullTask, setFullTask] = useState<Task | null>(null);
  const [taskDetailLoading, setTaskDetailLoading] = useState(false);
  // THUB-161: feed the History tab + Overview metrics on this surface too — not
  // just the Tasks page. Without this, opening a task from the Partner page
  // showed "0 executions" regardless of run history.
  const [taskExecutions, setTaskExecutions] = useState<TaskExecution[]>([]);
  // THUB-231: the Edit action opens a nested <TaskEditor>, mirroring TaskList.
  const [taskToEdit, setTaskToEdit] = useState<Task | null>(null);

  const closeTaskDetail = useCallback(() => {
    setSelectedTaskId(null);
    setFullTask(null);
    setTaskDetailLoading(false);
    setTaskExecutions([]);
  }, []);

  const openTaskDetail = useCallback((taskId: string) => {
    setSelectedTaskId(taskId);
    setFullTask(null);
    setTaskExecutions([]);
    setTaskDetailLoading(true);
    tasksApi
      .getTask(taskId)
      .then(detail => {
        setFullTask(detail);
      })
      .catch(err => {
        console.error('Failed to load task detail:', err);
        toast.error('Could not load task details. Please try again.');
        closeTaskDetail();
      })
      .finally(() => {
        setTaskDetailLoading(false);
      });
    // Executions load independently (best-effort) — the History tab populates
    // when they arrive; a failure just leaves it empty, doesn't block the modal.
    tasksApi
      .getExecutionsForTask(taskId, 50)
      .then(setTaskExecutions)
      .catch(err => {
        console.error('Failed to load task executions:', err);
        setTaskExecutions([]);
      });
  }, [closeTaskDetail]);

  // Version conflict handling - temporarily disabled for debugging
  // const {
  //   hasConflict,
  //   conflictState,
  //   startTracking,
  //   stopTracking,
  //   refresh: refreshVersion,
  //   clearConflict,
  // } = useVersionConflict({
  //   entityType: 'partner',
  //   onConflict: (state) => {
  //     console.log('Partner version conflict detected:', state);
  //   },
  //   onExternalUpdate: (version) => {
  //     console.log('Partner updated externally:', version);
  //   },
  // });

  // Temporary stub values - temporarily disabled for debugging
  // const hasConflict = false;
  // const conflictState = { serverVersion: '0', localVersion: '0', lastModifiedAt: undefined, lastModifiedBy: undefined };
  // const showConflictToast = false;
  // const clearConflict = () => {};

  // State for showing conflict toast - disabled
  // const [showConflictToast, setShowConflictToast] = useState(false);


  // Manual refresh. The reads are the queries' job now; this only adds the parent notification
  // that the non-preserveState callers depend on.
  const refreshDependencies = useCallback(async (preserveState: boolean = true) => {
    await Promise.all([connectionsQuery.refetch(), tasksQuery.refetch()]);
    if (!preserveState) {
      onUpdate();
    }
  }, [connectionsQuery, tasksQuery, onUpdate]);

  // THUB-299: THUB-182 subscribed to task/connection topics here and re-ran a local fetch
  // through useCoalescedCallback, because -- in its own words -- these lists lived in
  // component-local useState, "entirely outside TanStack", so App.tsx's invalidateQueries could
  // never reach them and the open modal went stale whenever a task or connection changed
  // elsewhere.
  //
  // They are now queries keyed under `connections` / `tasks`, and App.tsx already subscribes to
  // those topics and flushes the matching keys coalesced onto one trailing edge. The reach is
  // native and the coalescing is central, so a local subscription would only duplicate both.

  // THUB-231: real task actions. These callbacks used to be console.log stubs,
  // so confirming a task deletion here silently did nothing.
  const taskActions = useTaskActions({
    onChanged: () => refreshDependencies(true),
  });

  // THUB-231: save handler for the nested <TaskEditor> (Edit action).
  const handleUpdateTask = useCallback(async (taskId: string, taskData: UpdateTaskRequest) => {
    try {
      await tasksApi.updateTask(taskId, taskData);
      setTaskToEdit(null);
      closeTaskDetail();
      toast.success('Task update in progress');
      await refreshDependencies(true);
    } catch (error) {
      console.error('Failed to update task:', error);
      throw error; // let TaskEditor surface the error inline
    }
  }, [closeTaskDetail, refreshDependencies]);

  const handleValidateTaskConfiguration = useCallback(async (
    transferDirection: TransferDirection,
    configuration: any
  ) => {
    try {
      return await tasksApi.validateTaskConfiguration(transferDirection, configuration);
    } catch (error) {
      console.warn('Configuration validation unavailable:', error);
      return {
        valid: true,
        errors: ['Configuration validation unavailable due to network issues'],
      };
    }
  }, []);

  // Track partner version for conflict detection - temporarily disabled
  // useEffect(() => {
  //   try {
  //     if (partner.partnerId && typeof partner.version === 'number') {
  //       startTracking(partner.partnerId, {
  //         version: partner.version,
  //         updatedAt: partner.updatedAt || new Date().toISOString(),
  //         updatedBy: undefined, // Partner type doesn't have updatedBy field
  //       });
  //     }
  //   } catch (error) {
  //     console.warn('Failed to start version tracking for partner:', error);
  //   }
  //   return () => {
  //     try {
  //       stopTracking();
  //     } catch (error) {
  //       console.warn('Failed to stop version tracking:', error);
  //     }
  //   };
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [partner.partnerId, partner.version]);

  // Show conflict toast when conflict detected - disabled
  // useEffect(() => {
  //   if (hasConflict) {
  //     setShowConflictToast(true);
  //   }
  // }, [hasConflict]);

  // Handle conflict refresh - temporarily disabled for debugging
  // const handleConflictRefresh = useCallback(async () => {
  //   clearConflict();
  //   await onUpdate();
  // }, [clearConflict, onUpdate]);

  // Manual refresh pattern - no real-time updates

  const formatBytes = (bytes: number): string => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Check if partner has dependencies (used for UI hints)
  const hasConnections = partnerConnections.length > 0;
  const hasTasks = partnerTasks.length > 0;

  // Attempt to delete partner (without cascade first)
  const handleDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await partnersApi.deletePartner(partner.partnerId, { version: partner.version });
      setShowDeleteConfirm(false);
      // Close modal immediately - manual refresh required to see removal
      onClose();
    } catch (err) {
      // Handle DependencyError - show cascade dialog with actual resource names
      if (err instanceof DependencyError) {
        // Build dependency list from already-loaded connections and tasks
        // so the cascade dialog shows real names and IDs instead of placeholders
        const realDependencies: Dependency[] = [
          ...partnerConnections.map(conn => ({
            type: 'connection' as const,
            id: conn.connectionId,
            name: conn.name
          })),
          ...partnerTasks.map(task => ({
            type: 'task' as const,
            id: task.taskId,
            name: task.name
          }))
        ];
        // Use real dependencies if available, otherwise fall back to what the error provides
        setCascadeDependencies(realDependencies.length > 0 ? realDependencies : err.dependencies);
        setShowDeleteConfirm(false);
        setShowCascadeDialog(true);
        setIsDeleting(false);
        return;
      }

      const errorMessage = err instanceof Error ? err.message : 'Failed to delete partner';
      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  // Handle cascade delete confirmation
  const handleCascadeDelete = async () => {
    setIsDeleting(true);
    setDeleteError(null);

    try {
      await partnersApi.deletePartner(partner.partnerId, { cascade: true, version: partner.version });
      setShowCascadeDialog(false);
      // Close modal - manual refresh required to see the removal
      onClose();
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to delete partner';
      setDeleteError(errorMessage);
      setIsDeleting(false);
    }
  };

  // Use API-loaded counts when available, otherwise fall back to partner record counts
  const connectionsCount = partnerConnections.length > 0 || !dependenciesLoading
    ? partnerConnections.length
    : (partner.connections?.length ?? 0);
  const tasksCount = partnerTasks.length > 0 || !dependenciesLoading
    ? partnerTasks.length
    : (partner.taskCounts?.total ?? partner.tasks?.length ?? 0);

  const tabs: { id: TabType; label: string; count?: number }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'connections', label: 'Connections', count: connectionsCount },
    { id: 'tasks', label: 'Tasks', count: tasksCount },
    { id: 'activity', label: 'Activity' },
    { id: 'audit', label: 'Audit History' },
  ];

  const renderOverviewTab = () => {
    const last24Hours = partner.activitySummary?.last24Hours || {
      filesReceived: 0,
      filesSent: 0,
      bytesTransferred: 0,
      successfulTransfers: 0,
      failedTransfers: 0
    };

    return (
      <div className="h-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Basic Information */}
          <div className="bg-neutral-50 dark:bg-slate-800 rounded-lg p-5">
            <h4 className="text-base font-semibold text-neutral-900 dark:text-slate-100 mb-4">
              Basic Information
            </h4>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Partner Name
                </label>
                <span className="text-sm font-medium text-neutral-900 dark:text-slate-100">
                  {partner.name}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Partner ID
                </label>
                <span className="text-sm font-medium text-neutral-900 dark:text-slate-100 font-mono text-xs">
                  {partner.partnerId}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Description
                </label>
                <span className="text-sm font-medium text-neutral-900 dark:text-slate-100">
                  {partner.description || 'No description'}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Created By
                </label>
                <span className="text-sm font-medium text-neutral-900 dark:text-slate-100">
                  {partner.createdBy}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Active
                </label>
                <StatusIndicator
                  status={partner.isActive ? 'success' : 'error'}
                  label={partner.isActive ? 'Yes' : 'No'}
                />
              </div>
              <div className="flex flex-col gap-1">
                <label className="text-xs font-medium text-neutral-500 dark:text-slate-400 uppercase tracking-wider">
                  Status
                </label>
                <ResourceStatusBadge
                  status={(partner.provisioningStatus || 'active') as ProvisioningStatus}
                  size="sm"
                />
              </div>
            </div>
          </div>

          {/* Task Summary */}
          <div className="bg-neutral-50 dark:bg-slate-800 rounded-lg p-5">
            <h4 className="text-base font-semibold text-neutral-900 dark:text-slate-100 mb-4">
              Task Summary
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md">
                <span className="text-2xl font-bold text-primary-500">{partner.taskCounts?.total || 0}</span>
                <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Total</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md">
                <span className="text-2xl font-bold text-success-500">{partner.taskCounts?.enabled || 0}</span>
                <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Enabled</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md">
                <span className="text-2xl font-bold text-warning-500">{partner.taskCounts?.running || 0}</span>
                <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Running</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md">
                <span className="text-2xl font-bold text-neutral-400">{partner.taskCounts?.disabled || 0}</span>
                <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Disabled</span>
              </div>
            </div>
          </div>

          {/* AWS Resources & Provisioning (collapsible) */}
          <CollapsibleSection title="AWS Resources" defaultExpanded={false}>
            <div className="flex flex-col gap-3">
              <div className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 rounded">
                <label className="text-sm font-medium text-neutral-600 dark:text-slate-400">S3 Bucket</label>
                <span className={`text-sm font-medium ${partner.bucketName ? 'text-success-600 dark:text-success-400' : 'text-neutral-400 dark:text-slate-500 italic'}`}>
                  {partner.bucketName || 'Not provisioned'}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 rounded">
                <label className="text-sm font-medium text-neutral-600 dark:text-slate-400">KMS Key</label>
                <span className={`text-sm font-medium ${partner.kmsKeyArn ? 'text-success-600 dark:text-success-400' : 'text-neutral-400 dark:text-slate-500 italic'}`}>
                  {partner.kmsKeyArn ? 'Configured' : 'Not provisioned'}
                </span>
              </div>
              <div className="flex justify-between items-center p-2 bg-white dark:bg-slate-800 rounded">
                <label className="text-sm font-medium text-neutral-600 dark:text-slate-400">SNS Topic</label>
                <span className={`text-sm font-medium ${partner.snsTopicArn ? 'text-success-600 dark:text-success-400' : 'text-neutral-400 dark:text-slate-500 italic'}`}>
                  {partner.snsTopicArn ? 'Configured' : 'Not provisioned'}
                </span>
              </div>
            </div>
          </CollapsibleSection>

          {/* Connections (collapsible) */}
          <CollapsibleSection title="Connections" count={partner.connections?.length || 0} defaultExpanded={false}>
            {partner.connections && partner.connections.length > 0 ? (
              <div className="flex flex-col gap-2">
                {partner.connections.map(conn => (
                  <div key={conn.connectionId} className="flex items-center justify-between p-2 bg-white dark:bg-slate-800 rounded text-sm">
                    <span className="font-medium text-neutral-900 dark:text-white">{conn.name}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-neutral-500 dark:text-slate-400">
                        {conn.hostingType} / {conn.transferMethod}
                      </span>
                      <ResourceStatusBadge status={(conn.provisioningStatus || 'active') as any} size="sm" />
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-neutral-500 dark:text-slate-400 italic">No connections</p>
            )}
          </CollapsibleSection>

          {/* Recent Activity (24h) */}
          <div className="bg-neutral-50 dark:bg-slate-800 rounded-lg p-5">
            <h4 className="text-base font-semibold text-neutral-900 dark:text-slate-100 mb-4">
              Recent Activity (24h)
            </h4>
            <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md min-h-[72px] justify-center">
                <span className="text-lg font-bold text-neutral-900 dark:text-slate-100">{last24Hours.filesReceived}</span>
                <span className="text-[10px] text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Files Received</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md min-h-[72px] justify-center">
                <span className="text-lg font-bold text-neutral-900 dark:text-slate-100">{last24Hours.filesSent}</span>
                <span className="text-[10px] text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Files Sent</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md min-h-[72px] justify-center">
                <span className="text-lg font-bold text-neutral-900 dark:text-slate-100">{formatBytes(last24Hours.bytesTransferred || 0)}</span>
                <span className="text-[10px] text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Data Transferred</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md min-h-[72px] justify-center">
                <span className="text-lg font-bold text-success-500">{last24Hours.successfulTransfers}</span>
                <span className="text-[10px] text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Successful</span>
              </div>
              <div className="flex flex-col items-center text-center p-3 bg-white dark:bg-slate-800 rounded-md min-h-[72px] justify-center">
                <span className="text-lg font-bold text-danger-500">{last24Hours.failedTransfers}</span>
                <span className="text-[10px] text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-1">Failed</span>
              </div>
            </div>
          </div>

        </div>
      </div>
    );
  };

  const renderActivityTab = () => {
    const last30Days = partner.activitySummary?.last30Days || {
      totalTransfers: 0,
      averageDaily: 0,
      peakDay: '',
      transfersByDirection: {}
    };

    const allZero = last30Days.totalTransfers === 0 && last30Days.averageDaily === 0;
    if (allZero) {
      return (
        <div className="flex flex-col items-center justify-center py-16 gap-3">
          <p className="text-sm text-neutral-500 dark:text-slate-400">No transfer data available yet</p>
          <a href="/transfer-history" className="text-sm text-primary-600 dark:text-primary-400 hover:underline">
            View recent file events in Transfer History
          </a>
        </div>
      );
    }

    const transfersByDirection = last30Days.transfersByDirection || {};
    const transferValues = Object.values(transfersByDirection);
    const maxTransfers = transferValues.length > 0 ? Math.max(...transferValues) : 1;

    return (
      <div className="h-full space-y-8">
        {/* 30-Day Activity Summary */}
        <div>
          <h4 className="text-lg font-semibold text-neutral-900 dark:text-slate-100 mb-4">
            30-Day Activity Summary
          </h4>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <div className="flex flex-col items-center text-center p-5 bg-neutral-50 dark:bg-slate-800 rounded-lg">
              <span className="text-3xl font-bold text-primary-500">{last30Days.totalTransfers}</span>
              <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-2">Total Transfers</span>
            </div>
            <div className="flex flex-col items-center text-center p-5 bg-neutral-50 dark:bg-slate-800 rounded-lg">
              <span className="text-3xl font-bold text-primary-500">{last30Days.averageDaily}</span>
              <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-2">Daily Average</span>
            </div>
            <div className="flex flex-col items-center text-center p-5 bg-neutral-50 dark:bg-slate-800 rounded-lg">
              <span className="text-3xl font-bold text-primary-500">
                {last30Days.peakDay ? formatDateTime(last30Days.peakDay, activeTimezone(), 'dateOnly') : 'N/A'}
              </span>
              <span className="text-xs text-neutral-500 dark:text-slate-400 uppercase tracking-wider mt-2">Peak Day</span>
            </div>
          </div>
        </div>

        {/* Transfer Types Breakdown */}
        <div>
          <h4 className="text-lg font-semibold text-neutral-900 dark:text-slate-100 mb-4">
            Transfer Types Breakdown
          </h4>
          <div className="space-y-3">
            {Object.entries(transfersByDirection).length > 0 ? (
              Object.entries(transfersByDirection).map(([direction, count]) => (
                <div
                  key={direction}
                  className="grid grid-cols-[120px,60px,1fr] md:grid-cols-[120px,60px,1fr] items-center gap-3 p-3 bg-neutral-50 dark:bg-slate-800 rounded-md"
                >
                  <span className="text-sm font-medium text-neutral-900 dark:text-slate-100 capitalize">{direction}</span>
                  <span className="text-sm font-semibold text-primary-500 text-right">{count}</span>
                  <div className="h-2 bg-neutral-200 dark:bg-slate-700 rounded-full overflow-hidden">
                    <div
                      className="h-full bg-primary-500 rounded-full transition-all duration-300"
                      style={{ width: `${(count / maxTransfers) * 100}%` }}
                    />
                  </div>
                </div>
              ))
            ) : (
              <p className="text-neutral-500 dark:text-slate-400 text-center py-8 italic">
                No transfer data available
              </p>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderConnectionsTab = () => {
    if (dependenciesLoading) {
      return (
        <div className="flex items-center justify-center py-10">
          <div className="text-neutral-500 dark:text-slate-400">Loading connections...</div>
        </div>
      );
    }

    return (
      <div>
        <div className="flex justify-between items-center mb-4">
          <h4 className="text-lg font-semibold text-neutral-900 dark:text-slate-100">
            Partner Connections ({partnerConnections.length})
          </h4>
        </div>

        {partnerConnections.length === 0 ? (
          <div className="text-center py-10 text-neutral-500 dark:text-slate-400 italic">
            No connections found for this partner.
          </div>
        ) : (
          <PartnerConnectionList
            connections={partnerConnections}
            partners={[partner]}
            onConnectionSelect={(connection) => setSelectedConnection(connection)}
          />
        )}
      </div>
    );
  };

  return (
    <>
      {/* Main Modal Overlay */}
      <div
        className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1000] p-4"
        onClick={(e) => {
          if (e.target === e.currentTarget) {
            onClose();
          }
        }}
      >
        <div
          className="
            bg-white dark:bg-slate-900
            rounded-xl shadow-2xl
            w-full max-w-[1200px] max-h-[90vh]
            flex flex-col overflow-hidden
            border border-neutral-200 dark:border-slate-700
            relative
          "
          onClick={(e) => e.stopPropagation()}
        >
          {/* THUB-298: the commented-out VersionConflictToast / EntityUpdateIndicator block that sat
              here is gone, along with the components themselves. The feature could not be re-enabled
              by uncommenting: its driver hook (hooks/useVersionConflict) was already deleted as
              unreachable, so the components were orphans that still shipped in the bundle via the
              shared barrel. Git history has the original if version-conflict detection is revived. */}

          {/* Header */}
          <div className="
            flex flex-shrink-0 justify-between items-center
            px-6 py-5
            border-b border-neutral-200 dark:border-slate-700
            bg-neutral-50 dark:bg-slate-800
          ">
            <div className="flex items-center gap-3">
              <h2 className="text-xl font-semibold text-neutral-900 dark:text-slate-100">
                {partner.name}
              </h2>
              <ResourceStatusBadge
                status={(partner.provisioningStatus || 'active') as ProvisioningStatus}
              />
            </div>
            <div className="flex items-center gap-2">
              <RoleGuard minimumRole="admin">
                <Button
                  variant="secondary"
                  onClick={() => setIsEditing(true)}
                >
                  Edit Partner
                </Button>
              </RoleGuard>
              <RoleGuard minimumRole="system_admin">
                <Button
                  variant="danger"
                  onClick={() => setShowDeleteConfirm(true)}
                  title={hasConnections || hasTasks ? 'Delete partner and all dependencies' : 'Delete this partner'}
                >
                  Delete Partner
                </Button>
              </RoleGuard>
              <button
                className="
                  w-10 h-10 flex items-center justify-center
                  rounded-lg text-2xl
                  text-neutral-500 hover:text-neutral-900
                  dark:text-slate-400 dark:hover:text-slate-100
                  hover:bg-neutral-200 dark:hover:bg-slate-700
                  transition-colors duration-150
                "
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onClose();
                }}
              >
                ×
              </button>
            </div>
          </div>

          {/* Tabs */}
          <div className="
            flex flex-shrink-0 border-b border-neutral-200 dark:border-slate-700
            bg-neutral-50 dark:bg-slate-800
            overflow-x-auto
          ">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                className={`
                  px-5 py-3 text-sm font-medium
                  border-b-2 transition-all duration-150
                  whitespace-nowrap
                  ${activeTab === tab.id
                    ? 'text-primary-600 dark:text-primary-400 border-primary-500 bg-white dark:bg-slate-900'
                    : 'text-neutral-600 dark:text-slate-400 border-transparent hover:text-neutral-900 dark:hover:text-slate-200 hover:bg-neutral-100 dark:hover:bg-slate-800'
                  }
                `}
                onClick={() => setActiveTab(tab.id)}
              >
                {tab.label}
                {tab.count !== undefined && (
                  <span className="ml-1.5 text-xs opacity-70">({tab.count})</span>
                )}
              </button>
            ))}
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {activeTab === 'overview' && renderOverviewTab()}
            {activeTab === 'connections' && renderConnectionsTab()}
            {activeTab === 'tasks' && (
              <div>
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-lg font-semibold text-neutral-900 dark:text-slate-100">
                    Partner Tasks ({partnerTasks.length})
                  </h4>
                </div>

                {dependenciesLoading ? (
                  <div className="flex items-center justify-center py-10">
                    <div className="text-neutral-500 dark:text-slate-400">Loading tasks...</div>
                  </div>
                ) : partnerTasks.length === 0 ? (
                  <div className="text-center py-10 text-neutral-500 dark:text-slate-400 italic">
                    No tasks found for this partner.
                  </div>
                ) : (
                  <PartnerTaskList
                    tasks={partnerTasks}
                    onTaskSelect={(task) => openTaskDetail(task.taskId)}
                  />
                )}
              </div>
            )}
            {activeTab === 'activity' && renderActivityTab()}
            {activeTab === 'audit' && (
              <AuditLogViewer
                entityType="partner"
                entityId={partner.partnerId}
                title="Partner Change History"
              />
            )}
          </div>
        </div>
      </div>

      {/* Edit Partner Modal */}
      {isEditing && (
        <EditPartner
          partner={partner}
          onClose={() => setIsEditing(false)}
          onUpdate={async () => {
            setIsEditing(false);
            await onUpdate();
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div
          className="fixed inset-0 bg-black/60 dark:bg-black/80 flex items-center justify-center z-[1100] p-4"
          onClick={() => !isDeleting && setShowDeleteConfirm(false)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-lg shadow-xl
              w-full max-w-md overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-neutral-200 dark:border-slate-700 bg-neutral-50 dark:bg-slate-800">
              <h3 className="text-lg font-semibold text-danger-600 dark:text-danger-400">
                Delete Partner
              </h3>
            </div>
            <div className="p-5 space-y-3">
              <p className="text-sm text-neutral-900 dark:text-slate-100">
                Are you sure you want to delete partner <strong>{partner.name}</strong>?
              </p>
              <p className="text-sm text-neutral-600 dark:text-slate-400">
                This action cannot be undone. All associated AWS resources (S3 bucket, KMS key, SNS topics)
                will be cleaned up by the offboarding workflow.
              </p>
              {deleteError && (
                <div className="p-3 rounded-md bg-danger-50 dark:bg-danger-900/30 border border-danger-200 dark:border-danger-800 text-sm text-danger-700 dark:text-danger-300">
                  {deleteError}
                </div>
              )}
            </div>
            <div className="
              flex justify-end gap-3 px-5 py-4
              border-t border-neutral-200 dark:border-slate-700
              bg-neutral-50 dark:bg-slate-800
            ">
              <Button
                variant="secondary"
                onClick={() => setShowDeleteConfirm(false)}
                disabled={isDeleting}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? 'Deleting...' : 'Delete Partner'}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Cascade Delete Dialog - shown when partner has dependencies */}
      <CascadeDeleteDialog
        isOpen={showCascadeDialog}
        onClose={() => {
          setShowCascadeDialog(false);
          setDeleteError(null);
        }}
        onConfirm={handleCascadeDelete}
        entityType="partner"
        entityName={partner.name}
        dependencies={cascadeDependencies}
        isDeleting={isDeleting}
      />

      {/* Nested Connection Details Modal */}
      {selectedConnection && (
        <div
          className="fixed inset-0 bg-black/70 dark:bg-black/80 flex items-center justify-center z-[1200] p-4"
          onClick={() => setSelectedConnection(null)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[1400px] max-h-[95vh]
              overflow-hidden relative flex flex-col
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <ConnectionDetails
              connection={selectedConnection}
              partners={[partner]} // Pass current partner for context
              onClose={() => setSelectedConnection(null)}
              onUpdate={async () => {
                // Manual refresh to see changes
                console.log('Connection updated/deleted, triggering manual refresh...');
                await refreshDependencies(true); // Preserve state for smooth updates
              }}
            />
          </div>
        </div>
      )}

      {/* Nested Task Details Modal */}
      {selectedTaskId && (
        <div
          className="fixed inset-0 bg-black/70 dark:bg-black/80 flex items-center justify-center z-[1200] p-4"
          onClick={closeTaskDetail}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[1400px] max-h-[95vh]
              overflow-hidden relative flex flex-col
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            {/* THUB-162: gate on the full-task fetch so we never render the
                stale partial list object (which flashed "still provisioning"). */}
            {taskDetailLoading || !fullTask ? (
              <div className="flex items-center justify-center p-16">
                <div className="flex flex-col items-center gap-3 text-neutral-500 dark:text-slate-400">
                  <div className="w-8 h-8 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                  <span className="text-sm">Loading task details…</span>
                </div>
              </div>
            ) : (
            <TaskDetails
              task={fullTask}
              executions={taskExecutions}
              onClose={closeTaskDetail}
              isDeleting={taskActions.isDeleting}
              deleteError={taskActions.deleteError}
              // THUB-231: all four callbacks were console.log stubs. They now
              // reach the real API via useTaskActions.
              onEdit={(task) => {
                setTaskToEdit(task);
              }}
              onDelete={async (taskId) => {
                // fullTask.version drives optimistic locking — the full task is
                // what this modal renders, so it is the authoritative version.
                await taskActions.deleteTask(taskId, fullTask.version);
                closeTaskDetail();
              }}
              onToggleStatus={async (taskId, enabled) => {
                await taskActions.toggleTaskStatus(taskId, enabled);
              }}
              onExecute={async (taskId) => {
                await taskActions.executeTask(taskId);
              }}
            />
            )}
          </div>
        </div>
      )}

      {/* THUB-231: nested Task Editor for the Edit action (mirrors TaskList).
          PartnerDetails only holds the single `partner`, and it already passes
          partners={[partner]} down to ConnectionDetails, so [partner] here is
          consistent with the existing pattern. */}
      {taskToEdit && (
        <div
          className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-[1300] p-4"
          onClick={() => setTaskToEdit(null)}
        >
          <div
            className="
              bg-white dark:bg-slate-900
              rounded-xl shadow-2xl
              w-full max-w-[900px] max-h-[90vh]
              overflow-hidden
              border border-neutral-200 dark:border-slate-700
            "
            onClick={(e) => e.stopPropagation()}
          >
            <TaskEditor
              task={taskToEdit}
              partners={[{ id: partner.partnerId, name: partner.name }]}
              onSave={handleUpdateTask}
              onCancel={() => setTaskToEdit(null)}
              onValidateConfiguration={handleValidateTaskConfiguration}
            />
          </div>
        </div>
      )}
    </>
  );
}

export default PartnerDetails;
